import { ThemeDefinition, ResolvedMode, ColorGrade, USWDSColorFamily } from './types';
import { hexToHsl as hexToHslObject } from './color-utils';

/**
 * Design Token Bridge
 *
 * Connects the ThemeDefinition type system (9 color families × 7 grades) to the
 * Tailwind CSS custom properties that drive the UI. Converts hex colors to HSL
 * space-separated values since Tailwind's config uses `hsl(var(--token))`.
 */

/** Maps a CSS variable to a specific color family and grade per mode */
export interface TokenMapping {
  readonly cssVariable: string;
  readonly lightGrade: keyof ColorGrade;
  readonly darkGrade: keyof ColorGrade;
  readonly family: USWDSColorFamily;
}

/**
 * Converts a hex color to HSL space-separated format: "H S% L%"
 * Accepts 6-digit hex with or without leading '#'.
 */
export function hexToHsl(hex: string): string {
  const hsl = hexToHslObject(hex);
  return `${Math.round(hsl.h)} ${Math.round(hsl.s)}% ${Math.round(hsl.l)}%`;
}

/**
 * Maps ThemeDefinition color families to Tailwind CSS token variables.
 * Each CSS token is sourced from a specific family + grade combination
 * depending on whether light or dark mode is active.
 */
export const TOKEN_MAP: TokenMapping[] = [
  // Primary tokens
  { cssVariable: '--primary', family: 'primary', lightGrade: 'base', darkGrade: 'light' },
  { cssVariable: '--primary-foreground', family: 'base', lightGrade: 'lightest', darkGrade: 'lightest' },
  // Secondary tokens
  { cssVariable: '--secondary', family: 'secondary', lightGrade: 'base', darkGrade: 'light' },
  { cssVariable: '--secondary-foreground', family: 'base', lightGrade: 'lightest', darkGrade: 'lightest' },
  // Accent tokens
  { cssVariable: '--accent', family: 'accent-cool', lightGrade: 'base', darkGrade: 'light' },
  { cssVariable: '--accent-foreground', family: 'base', lightGrade: 'lightest', darkGrade: 'lightest' },
  // Surface tokens
  { cssVariable: '--background', family: 'base', lightGrade: 'lightest', darkGrade: 'darkest' },
  { cssVariable: '--foreground', family: 'base', lightGrade: 'darkest', darkGrade: 'lightest' },
  { cssVariable: '--card', family: 'base', lightGrade: 'lightest', darkGrade: 'darker' },
  { cssVariable: '--card-foreground', family: 'base', lightGrade: 'darkest', darkGrade: 'lightest' },
  { cssVariable: '--popover', family: 'base', lightGrade: 'lightest', darkGrade: 'darker' },
  { cssVariable: '--popover-foreground', family: 'base', lightGrade: 'darkest', darkGrade: 'lightest' },
  { cssVariable: '--muted', family: 'base', lightGrade: 'lighter', darkGrade: 'dark' },
  { cssVariable: '--muted-foreground', family: 'base', lightGrade: 'dark', darkGrade: 'lighter' },
  // Semantic tokens
  { cssVariable: '--destructive', family: 'error', lightGrade: 'base', darkGrade: 'light' },
  { cssVariable: '--destructive-foreground', family: 'base', lightGrade: 'lightest', darkGrade: 'lightest' },
  { cssVariable: '--warning', family: 'warning', lightGrade: 'base', darkGrade: 'light' },
  { cssVariable: '--warning-foreground', family: 'warning', lightGrade: 'darkest', darkGrade: 'darkest' },
  { cssVariable: '--success', family: 'success', lightGrade: 'base', darkGrade: 'light' },
  { cssVariable: '--success-foreground', family: 'base', lightGrade: 'lightest', darkGrade: 'lightest' },
  { cssVariable: '--info', family: 'info', lightGrade: 'base', darkGrade: 'light' },
  { cssVariable: '--info-foreground', family: 'base', lightGrade: 'lightest', darkGrade: 'lightest' },
  // Border/input/ring
  { cssVariable: '--border', family: 'base', lightGrade: 'lighter', darkGrade: 'dark' },
  { cssVariable: '--input', family: 'base', lightGrade: 'lighter', darkGrade: 'dark' },
  { cssVariable: '--ring', family: 'primary', lightGrade: 'base', darkGrade: 'light' },
];

/**
 * Generates a Map of CSS variable → HSL value for the given theme and mode.
 * Useful for testing without requiring DOM access.
 */
export function generateTokens(theme: ThemeDefinition, mode: ResolvedMode): Map<string, string> {
  const tokens = new Map<string, string>();

  for (const mapping of TOKEN_MAP) {
    const colorFamily = theme.colors[mapping.family];
    const grade = mode === 'light' ? mapping.lightGrade : mapping.darkGrade;
    const hexColor = colorFamily[grade];
    tokens.set(mapping.cssVariable, hexToHsl(hexColor));
  }

  return tokens;
}

/**
 * Applies a ThemeDefinition to the document root for the given mode.
 * Sets all CSS custom properties that Tailwind utilities consume.
 */
export function applyThemeTokens(theme: ThemeDefinition, mode: ResolvedMode): void {
  const root = document.documentElement;
  const tokens = generateTokens(theme, mode);

  for (const [variable, value] of tokens) {
    root.style.setProperty(variable, value);
  }
}
